import React, { useState, useEffect } from 'react';
import { Card, Button, Form, Nav, Badge, Spinner } from 'react-bootstrap';
import { FaTimes, FaSearch, FaCar, FaSync } from 'react-icons/fa';
import { RiSendPlaneLine } from "react-icons/ri";
import VehicleTripDetails from '../components/VehicleTripDetails';
import { GetFromApi } from '../utils/api';
import socketService from '../utils/socket';

const VehicleListSidebar = ({ onClose, onVehicleSelect }) => {
  const [allVehicles, setAllVehicles] = useState([]);
  const [searchTerm, setSearchTerm] = useState('');
  const [filterStatus, setFilterStatus] = useState('all');
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [showTripDetails, setShowTripDetails] = useState(false);
  const [tripDetailsVehicle, setTripDetailsVehicle] = useState(null);
  const [isLoading, setIsLoading] = useState(true);

  const fetchVehicles = async () => {
    try {
      setIsLoading(true);
      const response = await GetFromApi('/vehicles');
      if (response.success) {
        const vehiclesWithDetails = response.data.map(vehicle => ({
          ...vehicle,
          status: vehicle.speed > 3 ? 'running' : (vehicle.ignition ? 'idle' : 'stopped'),
          date: new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' }),
          duration: '0 Hr 0Min',
          source: vehicle.address || 'Current Location',
          destination: 'In Transit',
          progress: 0,
          completed: 0,
          total: 300,
          lastUpdated: vehicle.updated_at ? getTimeAgo(new Date(vehicle.updated_at)) : 'Just now',
          speed: vehicle.speed || 0,
          driver: vehicle.driver_name || 'N/A',
          fuelLevel: 100
        }));
        setAllVehicles(vehiclesWithDetails);
        
        // Select first vehicle by default
        if (vehiclesWithDetails.length > 0 && selectedVehicle === null) {
          setSelectedVehicle(vehiclesWithDetails[0].id);
          if (onVehicleSelect) {
            onVehicleSelect(vehiclesWithDetails[0].id);
          }
        }
      }
    } catch (error) {
      console.error('Error fetching vehicles:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const getTimeAgo = (date) => {
    const seconds = Math.floor((new Date() - date) / 1000);
    if (seconds < 60) return 'Just now';
    const minutes = Math.floor(seconds / 60);
    if (minutes < 60) return `${minutes} min ago`;
    const hours = Math.floor(minutes / 60);
    return `${hours} hr ago`;
  };

  useEffect(() => {
    fetchVehicles();

    // Subscribe to real-time updates
    socketService.connect();
    socketService.on('vehicle:location', (data) => {
      setAllVehicles(prev => prev.map(v => {
        if (v.id === data.vehicleId) {
          return {
            ...v,
            speed: data.speed,
            status: data.isMoving ? 'running' : (data.ignition ? 'idle' : 'stopped'),
            lastUpdated: 'Just now'
          };
        }
        return v;
      }));
    });

    return () => {
      socketService.off('vehicle:location');
    };
  }, []);

  const filteredVehicles = allVehicles.filter((vehicle) => {
    const matchStatus =
      filterStatus === 'all' || 
      (filterStatus === 'running' && vehicle.status === 'running') || 
      (filterStatus === 'stopped' && (vehicle.status === 'stopped' || vehicle.status === 'idle'));
    const matchSearch = vehicle.number
      .toLowerCase()
      .includes(searchTerm.toLowerCase());
    return matchStatus && matchSearch;
  });

  const runningCount = allVehicles.filter(v => v.status === 'running').length;
  const stoppedCount = allVehicles.filter(v => v.status === 'stopped' || v.status === 'idle').length;

  const handleVehicleSelect = (vehicleId) => {
    setSelectedVehicle(vehicleId);
    if (onVehicleSelect) {
      onVehicleSelect(vehicleId);
    }
  };

  const handleVehicleClick = (vehicle) => {
    setTripDetailsVehicle(vehicle);
    setShowTripDetails(true);
  };

  const handleCloseTripDetails = () => {
    setShowTripDetails(false);
    setTripDetailsVehicle(null);
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'running':
        return '#22C55E';
      case 'idle':
        return '#F59E0B'; // Orange/Yellow
      case 'stopped':
        return '#EF4444';
      default:
        return '#6B7280';
    }
  };

  return (
    <div className="position-absolute top-0 start-0 h-100 z-3" style={{ padding: '20px' }}>
      <Card className="border-0 rounded h-100 shadow" style={{ width: '340px', maxHeight: '100vh', borderRadius: '12px' }}>
        <Card.Header className="bg-white d-flex justify-content-between align-items-center py-3 px-3 border-0">
          <h6 className="mb-0 fw-semibold">
            All Vehicles ({allVehicles.length})
            {isLoading && <Spinner size="sm" className="ms-2" />}
          </h6>
          <div className="d-flex gap-2">
            <Button variant="link" className="p-0 text-secondary" onClick={fetchVehicles} disabled={isLoading}>
              <FaSync size={14} />
            </Button>
            <Button variant="link" className="p-0 text-secondary" onClick={onClose}>
              <FaTimes />
            </Button>
          </div>
        </Card.Header>

        <Card.Body className="pt-0 overflow-auto" style={{ maxHeight: 'calc(100vh - 120px)' }}>
          {/* Search */}
          <div className="position-relative mb-3">
            <FaSearch className="position-absolute" style={{ top: '12px', left: '12px', color: '#9CA3AF' }} />
            <Form.Control
              type="text"
              placeholder="Search vehicles..."
              value={searchTerm}
              onChange={(e) => setSearchTerm(e.target.value)}
              style={{ paddingLeft: '38px', borderRadius: '8px', border: '1px solid #E5E7EB' }}
            />
          </div>

          {/* Filter Tabs */}
          <Nav variant="pills" className="mb-3 gap-2">
            <Nav.Item>
              <Nav.Link 
                className={`px-3 py-1 ${filterStatus === 'all' ? 'bg-primary text-white' : 'bg-light text-dark'}`}
                onClick={() => setFilterStatus('all')}
                style={{ borderRadius: '6px', fontSize: '13px' }}
              >
                All ({allVehicles.length})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                className={`px-3 py-1 ${filterStatus === 'running' ? 'bg-success text-white' : 'bg-light text-dark'}`}
                onClick={() => setFilterStatus('running')}
                style={{ borderRadius: '6px', fontSize: '13px' }}
              >
                Running ({runningCount})
              </Nav.Link>
            </Nav.Item>
            <Nav.Item>
              <Nav.Link 
                className={`px-3 py-1 ${filterStatus === 'stopped' ? 'bg-danger text-white' : 'bg-light text-dark'}`}
                onClick={() => setFilterStatus('stopped')}
                style={{ borderRadius: '6px', fontSize: '13px' }}
              >
                Stopped ({stoppedCount})
              </Nav.Link>
            </Nav.Item>
          </Nav>

          {/* Vehicle List */}
          {isLoading ? (
            <div className="text-center py-5">
              <Spinner animation="border" variant="primary" />
              <p className="mt-2 text-muted">Loading vehicles...</p>
            </div>
          ) : filteredVehicles.length === 0 ? (
            <div className="text-center py-5 text-muted">
              <FaCar size={40} className="mb-3 opacity-50" />
              <p>No vehicles found</p>
            </div>
          ) : (
            filteredVehicles.map((vehicle) => (
              <Card 
                key={vehicle.id}
                className={`mb-2 border ${selectedVehicle === vehicle.id ? 'border-primary' : ''}`}
                style={{ 
                  borderRadius: '10px', 
                  cursor: 'pointer',
                  backgroundColor: selectedVehicle === vehicle.id ? 'rgba(59, 130, 246, 0.05)' : 'white'
                }}
                onClick={() => handleVehicleSelect(vehicle.id)}
              >
                <Card.Body className="p-3">
                  <div className="d-flex justify-content-between align-items-start mb-2">
                    <div className="d-flex align-items-center">
                      <div 
                        className="me-2 d-flex align-items-center justify-content-center"
                        style={{
                          width: 36,
                          height: 36,
                          borderRadius: 8,
                          backgroundColor: selectedVehicle === vehicle.id ? '#3B82F6' : '#EEF2F8'
                        }}
                      >
                        <FaCar size={16} color={selectedVehicle === vehicle.id ? 'white' : '#3B82F6'} />
                      </div>
                      <div>
                        <div className="fw-semibold" style={{ fontSize: '14px' }}>{vehicle.number}</div>
                        <div className="d-flex align-items-center gap-1">
                          <span 
                            style={{ 
                              width: 8, 
                              height: 8, 
                              borderRadius: '50%', 
                              backgroundColor: getStatusColor(vehicle.status),
                              display: 'inline-block'
                            }}
                          />
                          <small className="text-muted">{vehicle.speed} km/h</small>
                        </div>
                      </div>
                    </div>
                    <Badge 
                      bg={vehicle.status === 'running' ? 'success' : (vehicle.status === 'idle' ? 'warning' : 'danger')}
                      style={{ borderRadius: '4px', fontSize: '10px' }}
                    >
                      {vehicle.status === 'running' ? 'Running' : (vehicle.status === 'idle' ? 'Idle' : 'Stopped')}
                    </Badge>
                  </div>

                  <div className="d-flex justify-content-between align-items-center mb-2">
                    <small className="text-muted">Last Update: {vehicle.lastUpdated}</small>
                  </div>

                  <div className="mt-2 pt-2 border-top d-flex justify-content-between">
                    <small><strong>Driver:</strong> {vehicle.driver}</small>
                    <Button 
                      variant="outline-primary" 
                      size="sm" 
                      style={{ fontSize: '11px', padding: '2px 8px' }}
                      onClick={(e) => { e.stopPropagation(); handleVehicleClick(vehicle); }}
                    >
                      <RiSendPlaneLine className="me-1" /> Details
                    </Button>
                  </div>
                </Card.Body>
              </Card>
            ))
          )}
        </Card.Body>
      </Card>

      {/* Trip Details Modal */}
      {showTripDetails && tripDetailsVehicle && (
        <VehicleTripDetails vehicle={tripDetailsVehicle} onClose={handleCloseTripDetails} />
      )}
    </div>
  );
};

export default VehicleListSidebar;
